import { useEffect, useState } from 'react'
import SEOHead from '@/components/common/SEOHead'
import { CourseApi } from '@/services/courseApi'
import apiClient from '@/services/apiClient'
import type { PublicCourse } from '@/types/course'
import { ConsultZaloModal } from '@/components/home/v2'
import {
  EaseHero,
  EaseStats,
  EaseStageCards,
  EaseBaAiSection,
  EaseCompareTable,
  EaseReviews,
  EaseWhy,
  EaseFinalCta,
} from '@/components/home/ease'
import { useRevealOnScroll } from '@/hooks/useRevealOnScroll'
import type { GiftComboInfo } from '@/components/course/CourseComboPrice'
import { useAuthStore } from '@/store/authStore'

const HomePageEase = () => {
  const { isAuthenticated } = useAuthStore()
  const [courses, setCourses] = useState<PublicCourse[]>([])
  const [coursesLoading, setCoursesLoading] = useState(true)
  const [giftCombos, setGiftCombos] = useState<GiftComboInfo[]>([])
  const [giftCombosLoading, setGiftCombosLoading] = useState(true)
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<Set<number>>(new Set())
  const [showConsultZalo, setShowConsultZalo] = useState(false)
  const revealRef = useRevealOnScroll<HTMLDivElement>()

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setCoursesLoading(true)
        setGiftCombosLoading(true)
        const [res, combos] = await Promise.all([
          CourseApi.getCourses({ limit: 50, page: 1 }),
          CourseApi.getGiftCombos(),
        ])
        if (!cancelled) {
          setCourses(res.courses || [])
          setGiftCombos(combos || [])
        }
      } catch {
        if (!cancelled) {
          setCourses([])
          setGiftCombos([])
        }
      } finally {
        if (!cancelled) {
          setCoursesLoading(false)
          setGiftCombosLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      setEnrolledCourseIds(new Set())
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const response = await apiClient.get('/student/my-courses')
        const data = response.data
        if (cancelled) return
        if (data.success && Array.isArray(data.data)) {
          setEnrolledCourseIds(
            new Set(data.data.map((c: { id: number }) => c.id).filter(Boolean)),
          )
        } else {
          setEnrolledCourseIds(new Set())
        }
      } catch {
        if (!cancelled) setEnrolledCourseIds(new Set())
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated])

  return (
    <div ref={revealRef} className="bg-eh-cream font-body text-eh-charcoal min-h-full">
      <SEOHead
        title="M2MBA - Nâng Tầm Kỹ Năng Business Analyst Đón Đầu Công Nghệ"
        description="M2MBA đào tạo Business Analyst (BA) từ cơ bản đến nâng cao. Học IT cho BA, BA thực chiến, ứng dụng AI trong BA cùng chuyên gia hơn 10 năm kinh nghiệm. Được hàng ngàn học viên tin tưởng."
        image="https://storage.googleapis.com/m2mba/f4872d86-57b4-4bb8-9492-f14a60f22280.jpg"
        url="https://m2mba.com"
        keywords="khóa học business analyst, khóa học BA, đào tạo BA, BA cho người bắt đầu,AI,Automation Workflow, n8n,bmad method, API, Database, SQL, tích hợp hệ thống"
        tags={[
          'Business Analyst',
          'Khóa học BA',
          'Đào tạo BA',
          'AI',
          'Automation',
          'n8n',
          'API',
          'Database',
          'SQL',
          'tich hợp hệ thống',
        ]}
        noindex={false}
        type="website"
        author="M2MBA Team"
        publishedTime={new Date('2023-08-01T00:00:00Z').toISOString()}
      />

      <EaseHero />
      <EaseStats
        giftCombos={giftCombos}
        giftCombosLoading={giftCombosLoading}
        enrolledCourseIds={enrolledCourseIds}
      />
      <EaseStageCards courses={courses} giftCombos={giftCombos} loading={coursesLoading} />
      <EaseBaAiSection courses={courses} />
      <EaseCompareTable courses={courses} />
      <EaseReviews />
      <EaseWhy />
      <EaseFinalCta onConsult={() => setShowConsultZalo(true)} />

      <ConsultZaloModal open={showConsultZalo} onClose={() => setShowConsultZalo(false)} />
    </div>
  )
}

export default HomePageEase
